import { useLocation, Link } from "wouter";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Home,
  Users,
  Building2,
  Package,
  FileText,
  ShoppingCart,
  Truck,
  Settings,
  ClipboardList,
  ClipboardCheck,
  FileInput,
  FileUp,
  PackageCheck,
} from "lucide-react";

type RouteConfig = {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  parent?: string;
};

const routeConfig: Record<string, RouteConfig> = {
  "/": { label: "Dashboard", icon: Home },
  "/accounts": { label: "Customers", icon: Building2 },
  "/contacts": { label: "Contacts", icon: Users },
  "/leads": { label: "Leads", icon: Users },
  "/opportunities": { label: "Opportunities", icon: FileText },
  "/products": { label: "Products", icon: Package },
  "/quotes": { label: "Sales Quotes", icon: FileText },
  "/customer-pos": { label: "Customer POs", icon: FileUp },
  "/orders": { label: "Orders", icon: ShoppingCart },
  "/order-line-items": { label: "Line Items", icon: ShoppingCart },
  "/invoices": { label: "Invoices", icon: FileText },
  "/vendors": { label: "Vendors", icon: Truck },
  "/vendor-quotes": { label: "Vendor Quotes", icon: FileText },
  "/purchase-orders": { label: "Purchase Orders", icon: ClipboardList },
  "/goods-receipts": { label: "Goods Receipts", icon: ClipboardCheck },
  "/ap-invoices": { label: "Vendor Invoices", icon: FileInput },
  "/vendor-portal/catalogs": { label: "Catalog Upload", icon: PackageCheck },
  "/settings": { label: "Settings", icon: Settings },
};

function matchRoute(path: string): { config: RouteConfig; pattern: string } | null {
  if (routeConfig[path]) {
    return { config: routeConfig[path], pattern: path };
  }
  return null;
}

type BreadcrumbItem = {
  path: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
};

function buildBreadcrumbChain(path: string): BreadcrumbItem[] {
  const chain: BreadcrumbItem[] = [];
  const match = matchRoute(path);
  if (!match) {
    return chain;
  }

  let currentPath: string | undefined = path;
  let currentPattern: string | undefined = match.pattern;

  while (currentPath && currentPattern) {
    const routeConf: RouteConfig | undefined = routeConfig[currentPattern];
    if (routeConf) {
      chain.unshift({
        path: currentPath,
        label: routeConf.label,
        icon: routeConf.icon,
      });

      if (routeConf.parent) {
        currentPattern = routeConf.parent;
        currentPath = routeConf.parent;
      } else {
        break;
      }
    } else {
      break;
    }
  }

  return chain;
}

export function PageBreadcrumb() {
  const [location] = useLocation();

  const isAtHome = location === "/";

  if (isAtHome) {
    return null;
  }

  const chain = buildBreadcrumbChain(location);

  if (chain.length === 0) {
    return null;
  }

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link href="/">
              <Home className="h-4 w-4" />
            </Link>
          </BreadcrumbLink>
        </BreadcrumbItem>

        {chain.map((item, index) => {
          const isLast = index === chain.length - 1;
          const Icon = item.icon;

          return (
            <span key={item.path} className="contents">
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                {isLast ? (
                  <BreadcrumbPage>
                    <span className="flex items-center gap-1.5">
                      {Icon && <Icon className="h-3.5 w-3.5" />}
                      {item.label}
                    </span>
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link href={item.path}>
                      <span className="flex items-center gap-1.5">
                        {Icon && <Icon className="h-3.5 w-3.5" />}
                        {item.label}
                      </span>
                    </Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            </span>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
